import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { AccessibleFleet, Location, Session } from '@cobliteam/common-types';

import { isWebDataSuccess, type WebData } from '@cobliteam/api-lib';

import { useMap } from '@cobliteam/hooks';

import { Sidebar } from '../Sidebar';

declare global {
  interface Window {
    isSidebarOpen: boolean | null;
  }
}

interface SidebarWrapperProps {
  loginWebData: WebData<Session>;
  currentLocation: Location;
  changeUserFleet: (fleet: AccessibleFleet) => void;
  logout: () => void;
  accessibleFleetsWebData: WebData<AccessibleFleet[]>;
}

export const SidebarLegacyWrapper: React.FC<SidebarWrapperProps> = ({ loginWebData, currentLocation, changeUserFleet, logout, accessibleFleetsWebData }) => {
  const widthBreakpoint = 1300;

  const [autoMode, setAutoMode] = useState(true);
  const [isOpen, setIsOpen] = useState(window.innerWidth >= widthBreakpoint);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { map } = useMap();

  // save state of sidebar in local storage

  const updateLocalStorage = useCallback(() => {
    const preferences = localStorage.getItem('preferences');
    if (preferences) {
      const preferencesObject = JSON.parse(preferences);
      localStorage.setItem('preferences', JSON.stringify({ ...preferencesObject, isOpenSidebar: isOpen }));
    }
  }, [isOpen]);

  const handleResize = useCallback(() => setWindowWidth(window.innerWidth), []);

  const onToggle = useCallback(() => {
    const newIsOpenStatus = !isOpen;

    window.isSidebarOpen = newIsOpenStatus;
    setIsOpen(newIsOpenStatus);
  }, [isOpen]);

  useEffect(() => {
    updateLocalStorage();
    window.dispatchEvent(new Event('resize'));
  }, [isOpen]);

  useEffect(() => {
    updateLocalStorage();
    window.isSidebarOpen = isOpen;
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * The sidebar work in a few different ways:
   * 1) The user never toggled the sidebar: it will close when lower than the break point, open after breakpoint
   * 2) The user closed sidebar manually: resize won't change sidebar open state
   */
  useEffect(() => {
    if (autoMode) {
      const shouldClose = isOpen && windowWidth < widthBreakpoint;
      const shouldOpen = !isOpen && windowWidth >= widthBreakpoint;

      if (shouldClose || shouldOpen) {
        onToggle();
      }
    }
  }, [windowWidth, autoMode, isOpen, onToggle]);

  const handleManualToggle = () => {
    setAutoMode(false);
    onToggle();

    setTimeout(() => {
      map?.invalidateSize({ pan: false });
    }, 500);
  };

  const getUserEmail = () => (isWebDataSuccess(loginWebData) ? loginWebData.data.user.email : '');

  return (
    <Sidebar
      currentLocation={currentLocation}
      userEmail={getUserEmail()}
      open={isOpen}
      onToggleClick={handleManualToggle}
      loginWebData={loginWebData}
      changeUserFleet={changeUserFleet}
      logout={logout}
      accessibleFleetsWebData={accessibleFleetsWebData}
    />
  );
};
